import ZtServiceFactory from '../zt';

// Fixed four-slot Team geometry shared by the provider and its consumers; the slot count is a page contract, not a magic number.
export const TEAM_SLOT_COUNT = 4;
const INVALID_CONTEXT_MESSAGE = 'invalid team context';

function createInvalidContextError() {
  return new Error(INVALID_CONTEXT_MESSAGE);
}

function getZtService() {
  return ZtServiceFactory.getDefaultInstance();
}

function createEmptySlots(count) {
  return Array.from({ length: count }, () => null);
}

function normalizeMember(member, resolveAvatar) {
  if (!member || typeof member.nickname !== 'string' || typeof member.num !== 'number') {
    throw createInvalidContextError();
  }

  return {
    displayName: member.nickname,
    avatarNumber: member.num,
    // The live `/team_config` response already returns `is_self` although the generated contract does not document it yet; absence is tolerated.
    isSelf: member.is_self === 1,
    // Guest-only marker (2026-09-10 contract): the member matching the invite `share_code` is the inviter; `/team_config` never returns it and absence is tolerated.
    isInviter: member.is_inviter === 1,
    avatarUrl: typeof resolveAvatar === 'function' ? resolveAvatar(member.num) : ''
  };
}

/**
 * Places members into their fixed slots by the member `num` field (2026-09-10 user decision):
 * `num` 1~4 maps one-to-one onto slot/avatar-frame 1~4, so an unordered `team_members` array
 * can never shuffle which head frame a member lands in. Values outside 1~4 or duplicates are invalid contexts.
 * @param {Array} rawMembers Raw `team_members` array in API order.
 * @param {Function} resolveAvatar Optional `num` to local-resource resolver.
 * @returns {Array<Object|null>} Fixed four-slot array.
 */
function placeMembersBySlotNumber(rawMembers, resolveAvatar) {
  const slots = createEmptySlots(TEAM_SLOT_COUNT);

  rawMembers.forEach(member => {
    const normalized = normalizeMember(member, resolveAvatar);
    if (!Number.isInteger(normalized.avatarNumber) || normalized.avatarNumber < 1 || normalized.avatarNumber > TEAM_SLOT_COUNT) {
      throw createInvalidContextError();
    }
    if (slots[normalized.avatarNumber - 1]) {
      throw createInvalidContextError();
    }

    slots[normalized.avatarNumber - 1] = normalized;
  });

  return slots;
}

/**
 * Converts `/team_config` data into the fixed four-slot ViewModel consumed by Team.
 * Only the `is_self`-flagged member is identifiable as the signed-in user; other identity fields stay absent.
 * `isFull` is derived from the member count because the owner-side API supplies no `is_full`;
 * the guest-side API does, so its adapter can map the native field onto the same ViewModel slot.
 * @param {Object} rawContext Raw `/team_config` data.
 * @param {Function} resolveAvatar Optional `num` to local-resource resolver.
 * @returns {Object} Stable Team ViewModel.
 */
function normalizeTeamContext(rawContext, resolveAvatar) {
  if (!rawContext || typeof rawContext.team_id !== 'string' || !Array.isArray(rawContext.team_members) || rawContext.team_members.length > TEAM_SLOT_COUNT) {
    throw createInvalidContextError();
  }

  const members = placeMembersBySlotNumber(rawContext.team_members, resolveAvatar);

  return {
    teamId: rawContext.team_id,
    hasTeam: Boolean(rawContext.team_id),
    memberCount: rawContext.team_members.length,
    isFull: rawContext.team_members.length >= TEAM_SLOT_COUNT,
    canEditNickname: rawContext.is_nickname_modified === 0,
    members
  };
}

/**
 * Loads and maps the current Team context. `team_status` is intentionally ignored because it
 * describes the activity time window rather than Team membership.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional raw Team provider for unit tests.
 * @param {Function} options.resolveAvatar Optional `num` to local-resource resolver.
 * @returns {Promise<Object>} Stable Team ViewModel.
 */
export function loadTeamContext(options = {}) {
  const provider = options.provider || (() => getZtService().teamConfig());

  return Promise.resolve()
    .then(() => provider())
    .then(rawContext => normalizeTeamContext(rawContext, options.resolveAvatar));
}

/**
 * Creates a Team without exposing the mutation response; the owning Store refreshes Team context.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional mutation provider for unit tests.
 * @returns {Promise<void>} Resolves after the request succeeds.
 */
export function createTeam(options = {}) {
  const provider = options.provider || (mockPassportAccount => getZtService().createTeam(mockPassportAccount));

  return Promise.resolve()
    .then(() => provider(undefined))
    .then(() => undefined);
}

/**
 * Updates the account nickname without selecting a member slot; the API supplies no current-user
 * member identifier. The owning Store refreshes Team context after success.
 * @param {string} displayName Nickname entered by the user.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional mutation provider for unit tests.
 * @returns {Promise<void>} Resolves after the request succeeds.
 */
export function updateTeamNickname(displayName, options = {}) {
  const provider = options.provider || ((mockPassportAccount, nickname) => getZtService().updateMemberMsg(mockPassportAccount, nickname));

  return Promise.resolve()
    .then(() => provider(undefined, displayName))
    .then(() => undefined);
}

/**
 * Loads the invited Team as a read-only four-slot ViewModel.
 * Native count/full/status fields remain authoritative so Guest does not infer server state.
 * @param {string} teamId Team ID captured from the invite route.
 * @param {string} [shareCode] Inviter share code captured from the invite route (2026-09-10 contract);
 * the backend marks the matching member with `is_inviter` — omitted when the invite carries no code.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional raw Guest Team provider for unit tests.
 * @param {Function} options.resolveAvatar Optional `num` to local-resource resolver.
 * @returns {Promise<Object>} Stable read-only Guest Team ViewModel.
 */
export function loadGuestTeamContext(teamId, shareCode, options = {}) {
  if (typeof teamId !== 'string' || !teamId) {
    return Promise.reject(createInvalidContextError());
  }

  const provider =
    options.provider ||
    ((mockPassportAccount, invitedShareCode, invitedTeamId) => getZtService().getTeamInfoById(mockPassportAccount, invitedShareCode, invitedTeamId));

  return Promise.resolve()
    .then(() => provider(undefined, typeof shareCode === 'string' ? shareCode : undefined, teamId))
    .then(rawContext => {
      if (
        !rawContext ||
        rawContext.team_id !== teamId ||
        !Array.isArray(rawContext.team_members) ||
        rawContext.team_members.length > TEAM_SLOT_COUNT ||
        !Number.isInteger(rawContext.member_count) ||
        rawContext.member_count !== rawContext.team_members.length ||
        (rawContext.is_full !== 0 && rawContext.is_full !== 1) ||
        (rawContext.is_full === 1) !== (rawContext.member_count === TEAM_SLOT_COUNT) ||
        !Number.isInteger(rawContext.team_status) ||
        rawContext.team_status < 0 ||
        rawContext.team_status > 2
      ) {
        throw createInvalidContextError();
      }

      // Members land on slots by their `num` field (same rule as the owner-side adapter), so API order cannot shuffle frames.
      return {
        teamId: rawContext.team_id,
        memberCount: rawContext.member_count,
        isFull: rawContext.is_full === 1,
        teamStatus: rawContext.team_status,
        members: placeMembersBySlotNumber(rawContext.team_members, options.resolveAvatar)
      };
    });
}

/**
 * Joins an invited Team while hiding mutation metadata from callers.
 * @param {string} teamId Team ID captured from the invite route.
 * @param {Object} options Adapter dependencies.
 * @param {Function} options.provider Optional mutation provider for unit tests.
 * @returns {Promise<void>} Resolves only after the formal join request succeeds.
 */
export function joinGuestTeam(teamId, options = {}) {
  if (typeof teamId !== 'string' || !teamId) {
    return Promise.reject(createInvalidContextError());
  }

  const provider = options.provider || ((mockPassportAccount, invitedTeamId) => getZtService().joinTeam(mockPassportAccount, invitedTeamId));

  return Promise.resolve()
    .then(() => provider(undefined, teamId))
    .then(() => undefined);
}

export default {
  loadTeamContext,
  createTeam,
  updateTeamNickname,
  loadGuestTeamContext,
  joinGuestTeam
};
